import React, { useState, useRef, useEffect, useCallback } from "react";
import Footer from "./Footer";
import { isLoginHSProduct } from "../config/product";
import { CONSENT_TITLE, CONSENT_LINES, savePendingConsent, lookupBusinessPublic, registerCompanyAtSignup, MEMBER_ROLES } from "../audit/disclosureConsent";
import TermsOfServiceBody from "./TermsOfServiceBody";
import { reportSignupConversion } from "../adsConversion";

// ──────────────────────────────────────────────────────
// SignupPage.jsx — 3단계: 시작(SNS 즉시가입 우선) → 정보입력 → 이메일인증
//   · SNS(구글/네이버/카카오) = 1탭 가입 (백엔드 자동가입) — 최상단 배치
//   · 개인/기업 선택은 별도 단계 대신 정보입력의 토글로 흡수
//   · 톤 = LoginHS 절제(검정·회색) — 그라데이션/보라 금지
// ──────────────────────────────────────────────────────

const INK = "#16151a";
const DARK = "#1b1a20";

const SignupPage = ({ API_BASE_URL, onNavigate }) => {
  const BRAND = isLoginHSProduct() ? "LoginHS" : "DocuAI";
  // -- Process State --
  const [step, setStep] = useState("start"); // 'start' | 'form' | 'auth'
  const [isCompany, setIsCompany] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreeYn, setAgreeYn] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  // 63차: 신고분포 정보공개 동의(선택, 기업/사업자·LoginHS 브랜드만). 토큰이 없어 로그인 뒤 적재된다.
  const [agreeDisclosure, setAgreeDisclosure] = useState(false);
  const [showDisclosureText, setShowDisclosureText] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // -- SNS providers --
  const [oauthProviders, setOauthProviders] = useState(null);
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/auth/oauth-providers`)
      .then((r) => r.json())
      .then((d) => setOauthProviders(d.providers || null))
      .catch(() => setOauthProviders(null));
  }, [API_BASE_URL]);
  const hasSns =
    oauthProviders &&
    (oauthProviders.google || oauthProviders.naver || oauthProviders.kakao);

  // -- User Info --
  const [userEmail, setUserEmail] = useState("");
  const [userEmailValid, setUserEmailValid] = useState(false);
  const [userName, setUserName] = useState("");
  const [userNameValid, setUserNameValid] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [businessNumber, setBusinessNumber] = useState("");
  const [businessNumberValid, setBusinessNumberValid] = useState(false);
  const [corpName, setCorpName] = useState("");
  const [corpCeo, setCorpCeo] = useState("");
  const [contactName, setContactName] = useState("");
  const [corpAddr, setCorpAddr] = useState("");
  // 72차 보강 3: 사업자 회원 구분(화주/관세사/둘 다)과 조회로 받은 부가 정보 — 가입 직후 회사 표 등록에 쓴다.
  const [memberRole, setMemberRole] = useState("");
  const lookupInfoRef = useRef(null);

  // -- Auth --
  const [authNum, setAuthNum] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isRunning || timeLeft <= 0) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setTimeLeft((p) => p - 1), 1000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [timeLeft, isRunning]);

  const startTimer = () => {
    setTimeLeft(300);
    setIsRunning(true);
  };
  const formatTime = (s) =>
    `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;

  // -- Handlers --
  const handleEmailInput = (v) => {
    setUserEmailValid(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v),
    );
    setUserEmail(v);
  };

  const handleNameInput = (v) => {
    setUserNameValid(v.trim().length >= 1);
    setUserName(v);
  };

  // 71차: 사업자번호를 다 넣으면 관세청·국세청에서 회사 정보를 가져와 채운다(말소 거래처 등록과 같은 원천).
  //   비어 있는 칸만 채운다 — 사용자가 이미 적은 값은 덮어쓰지 않는다. 실패해도 가입은 그대로 진행된다(직접 입력).
  const [bizLookup, setBizLookup] = useState({ state: "idle", msg: "" }); // idle | loading | done | fail
  const lookupBiz = useCallback(async (formatted) => {
    const digits = String(formatted || "").replace(/\D/g, "");
    if (digits.length !== 10) return;
    setBizLookup({ state: "loading", msg: "" });
    try {
      const r = await lookupBusinessPublic(digits);
      if (!r.found) { setBizLookup({ state: "fail", msg: r.message || "조회 결과가 없습니다. 직접 입력해 주세요." }); return; }
      const info = r.info || {};
      lookupInfoRef.current = info;
      const fill = (cur, setter, v) => { if (!String(cur || "").trim() && String(v || "").trim()) setter(String(v).trim()); };
      fill(corpName, setCorpName, info.corp_name);
      fill(corpCeo, setCorpCeo, info.ceo_name);
      fill(corpAddr, setCorpAddr, info.corp_addr);
      setBizLookup({ state: "done", msg: `${info.corp_name}${info.nts_status ? ` · 국세청 ${info.nts_status}` : ""}${info.customs_office ? ` · ${info.customs_office}` : ""}` });
    } catch (e) {
      setBizLookup({ state: "fail", msg: e.status === 429 ? "조회가 너무 잦습니다. 잠시 후 다시 시도해 주세요." : "조회하지 못했습니다. 직접 입력해 주세요." });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corpName, corpCeo, corpAddr]);

  const handleBusinessNumberInput = (v) => {
    const nums = v.replace(/\D/g, "").slice(0, 10);
    const formatted = nums
      .replace(/(\d{3})(\d{2})(\d{0,5})/, "$1-$2-$3")
      .trim();
    const valid = /^\d{3}-\d{2}-\d{5}$/.test(formatted);
    setBusinessNumberValid(valid);
    setBusinessNumber(formatted);
    if (valid) lookupBiz(formatted);
    else if (bizLookup.state !== "idle") setBizLookup({ state: "idle", msg: "" });
  };

  const handleTypeSwitch = (flag) => {
    if (flag === isCompany) return;
    setUserName("");
    setUserNameValid(false);
    setBusinessNumber("");
    setBusinessNumberValid(false);
    setCorpName("");
    setCorpCeo("");
    setContactName("");
    setCorpAddr("");
    setIsCompany(flag);
  };

  // -- SNS 즉시가입 (로그인과 동일 엔드포인트 — 미가입 시 자동가입) --
  const snsStart = (provider) => {
    window.location.href = `${API_BASE_URL}/api/auth/${provider}`;
  };

  // -- Send OTP email --
  const sendAuthEmail = useCallback(async () => {
    if (!userEmailValid) {
      alert("올바른 이메일 형식이 아닙니다");
      return false;
    }
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, type: "회원가입" }),
      });
      const data = await res.json();
      if (data.result || data.success) {
        setStep("auth");
        startTimer();
        return true;
      } else {
        alert(data.message || "인증번호 전송에 실패했습니다.");
        return false;
      }
    } catch (e) {
      alert("이메일 인증 전송 중 오류가 발생했습니다. (서비스 준비 중)");
      setStep("auth");
      startTimer();
      return false;
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, userEmail, userEmailValid]);

  // -- Submit signup --
  const handleSignup = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userEmail,
          userName,
          authNum,
          password,
          passwordConfirm,
          isCompany,
          businessNumber: businessNumber.replace(/-/g, ""),
          corpName,
          corpCeo,
          contactName,
          corpAddr,
          agreeMarketing,
        }),
      });
      const data = await res.json();
      if (data.result || data.success) {
        reportSignupConversion();
        if (isLoginHSProduct() && isCompany && agreeDisclosure && businessNumber.trim()) {
          savePendingConsent({ businessNumber, agreed: true });
        }
        // 72차 보강 3: 가입 서버는 회사 정보를 저장하지 않으므로 loginHS 회사 표에 검토 대기로 남긴다(실패해도 가입은 완료).
        if (isLoginHSProduct() && isCompany && data.data && data.data.userId) {
          const extra = lookupInfoRef.current || {};
          try {
            await registerCompanyAtSignup({
              group_id: data.data.groupCode,
              user_id: data.data.userId,
              business_no: businessNumber.replace(/\D/g, ""),
              role: memberRole,
              corp_name: corpName,
              ceo_name: corpCeo,
              contact_name: contactName,
              contact_email: userEmail,
              corp_addr: corpAddr,
              corp_name_en: extra.corp_name_en,
              customs_office: extra.customs_office,
              nts_status: extra.nts_status,
              biz_type: extra.biz_type,
              biz_item: extra.biz_item,
            });
          } catch (_e) {
            /* 로그인 뒤 회사 계정 화면에서 다시 요청할 수 있다 */
          }
        }
        alert("환영합니다! 로그인 페이지로 이동합니다.");
        onNavigate && onNavigate("login");
      } else {
        alert(data.message || "회원가입에 실패했습니다.");
      }
    } catch (e) {
      alert("회원가입 처리 중 오류가 발생했습니다. (서비스 준비 중)");
    } finally {
      setLoading(false);
    }
  };

  // -- Step transitions --
  const goForm = () => {
    if (!agreeYn) {
      alert("약관에 동의해주세요.");
      return;
    }
    if (!agreeMarketing) {
      alert("마케팅·홍보 정보 수신에 동의해야 무상 이용이 가능합니다.");
      return;
    }
    setStep("form");
  };

  const goAuth = async () => {
    if (!userEmailValid || !userEmail.trim()) {
      alert("이메일 형식이 올바르지 않습니다");
      return;
    }
    if (!password || password.length < 4) {
      alert("비밀번호는 4자 이상이어야 합니다.");
      return;
    }
    if (password !== passwordConfirm) {
      alert("비밀번호가 일치하지 않습니다.");
      return;
    }
    if (isCompany) {
      if (isLoginHSProduct() && !memberRole) return alert("회원 구분(화주·관세사)을 선택해주세요.");
      if (!corpName.trim()) return alert("회사명은 필수 입력값입니다");
      if (!businessNumber.trim())
        return alert("사업자등록번호를 입력해주세요.");
      if (!corpCeo.trim()) return alert("대표자명은 필수 입력값입니다");
      if (!contactName.trim()) return alert("담당자명은 필수 입력값입니다");
      if (!corpAddr.trim()) return alert("주소는 필수 입력값입니다");
    } else {
      if (!userNameValid || !userName.trim())
        return alert("이름을 입력해주세요.");
    }
    await sendAuthEmail();
  };

  // ─── Step 1: 시작 — SNS 즉시가입 + 이메일 가입 ───────────
  const renderStart = () => (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <h1 style={S.title}>시작하기</h1>
      <p style={S.subtitle}>가입은 무료이고, SNS 로는 클릭 한 번이면 됩니다</p>

      {hasSns && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            marginBottom: "14px",
          }}
        >
          {oauthProviders.google && (
            <button onClick={() => snsStart("google")} style={S.socialBtn}>
              <span
                style={{
                  ...S.socialIcon,
                  background: "#fff",
                  border: "1px solid #dadce0",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 48 48">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
              </span>
              <span>구글로 시작하기</span>
            </button>
          )}
          {oauthProviders.naver && (
            <button onClick={() => snsStart("naver")} style={S.socialBtn}>
              <span
                style={{
                  ...S.socialIcon,
                  background: "#03C75A",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "14px",
                }}
              >
                N
              </span>
              <span>네이버로 시작하기</span>
            </button>
          )}
          {oauthProviders.kakao && (
            <button onClick={() => snsStart("kakao")} style={S.socialBtn}>
              <span
                style={{
                  ...S.socialIcon,
                  background: "#FEE500",
                  color: "#3C1E1E",
                  fontWeight: "bold",
                  fontSize: "11px",
                }}
              >
                TALK
              </span>
              <span>카카오로 시작하기</span>
            </button>
          )}
          <p
            style={{
              fontSize: "12px",
              color: "#9ca3af",
              textAlign: "center",
              margin: "2px 0 0",
              lineHeight: 1.5,
            }}
          >
            SNS 가입 시 다음 화면에서 약관·마케팅 동의를 받습니다
          </p>
        </div>
      )}

      <div style={S.divider}>
        <div style={S.dividerLine} />
        <span style={S.dividerText}>또는 이메일로 가입</span>
        <div style={S.dividerLine} />
      </div>

      <label style={S.checkboxLabel}>
        <input
          type="checkbox"
          checked={agreeYn}
          onChange={(e) => setAgreeYn(e.target.checked)}
          style={{ marginRight: "8px", accentColor: DARK }}
        />
        <span>
          DocuAI·LoginHS 통합 이용약관에 동의합니다{" "}
          <button
            onClick={(e) => {
              e.preventDefault();
              setShowTerms((v) => !v);
            }}
            style={S.linkBtn}
          >
            {showTerms ? "접기" : "전문 보기"}
          </button>
        </span>
      </label>

      {showTerms && (
        <div style={S.termsScroll}>
          <TermsOfServiceBody />
        </div>
      )}

      {/* 무상 제공 조건 — 마케팅 동의 (정보통신망법상 별도 동의) */}
      <label style={{ ...S.checkboxLabel, marginTop: "8px" }}>
        <input
          type="checkbox"
          checked={agreeMarketing}
          onChange={(e) => setAgreeMarketing(e.target.checked)}
          style={{ marginRight: "8px", accentColor: DARK }}
        />
        <span>
          마케팅·홍보 정보 및 뉴스레터 수신에 동의합니다 (이메일·문자·전화 등){" "}
          <span style={{ color: "#9ca3af" }}>
            — HS 품목분류 무상 이용 조건 (수신 거부 가능)
          </span>
        </span>
      </label>

      <button onClick={goForm} style={{ ...S.primaryBtn, marginTop: "12px" }}>
        이메일로 가입
      </button>

      <div style={S.bottomLinks}>
        <span style={{ color: "#9ca3af" }}>이미 계정이 있으신가요?</span>
        <button
          onClick={() => onNavigate && onNavigate("login")}
          style={S.linkBtn}
        >
          로그인하기
        </button>
      </div>
    </div>
  );

  // ─── Step 2: 정보 입력 (개인/기업 토글 흡수) ─────────────
  const renderForm = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        animation: "fadeIn 0.3s ease",
      }}
    >
      <h1 style={{ ...S.title, fontSize: "22px" }}>정보 입력</h1>

      {/* 개인/기업 세그먼트 */}
      <div style={S.segmentWrap}>
        <button
          onClick={() => handleTypeSwitch(false)}
          style={{ ...S.segmentBtn, ...(isCompany ? {} : S.segmentActive) }}
        >
          개인
        </button>
        <button
          onClick={() => handleTypeSwitch(true)}
          style={{ ...S.segmentBtn, ...(isCompany ? S.segmentActive : {}) }}
        >
          기업/사업자
        </button>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "14px",
          flex: 1,
          marginBottom: "20px",
        }}
      >
        {!isCompany ? (
          <>
            <div>
              <label style={S.label}>이메일</label>
              <input
                value={userEmail}
                onChange={(e) => handleEmailInput(e.target.value)}
                placeholder="example@email.com"
                style={S.formInput}
              />
              {userEmail && !userEmailValid && (
                <span style={S.errorText}>이메일 형식이 올바르지 않습니다</span>
              )}
            </div>
            <div>
              <label style={S.label}>이름</label>
              <input
                value={userName}
                onChange={(e) => handleNameInput(e.target.value)}
                placeholder="홍길동"
                style={S.formInput}
              />
              {userName && !userNameValid && (
                <span style={S.errorText}>이름을 입력해주세요</span>
              )}
            </div>
            <div>
              <label style={S.label}>비밀번호</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="4자 이상"
                style={S.formInput}
              />
              {password && password.length < 4 && (
                <span style={S.errorText}>
                  비밀번호는 4자 이상이어야 합니다
                </span>
              )}
            </div>
            <div>
              <label style={S.label}>비밀번호 확인</label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="비밀번호 재입력"
                style={S.formInput}
              />
              {passwordConfirm && password !== passwordConfirm && (
                <span style={S.errorText}>비밀번호가 일치하지 않습니다</span>
              )}
            </div>
          </>
        ) : (
          <>
            {isLoginHSProduct() && (
              <div data-testid="signup-member-role">
                <label style={S.label}>회원 구분</label>
                <div style={S.segmentWrap} role="radiogroup" aria-label="회원 구분">
                  {MEMBER_ROLES.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      role="radio"
                      aria-checked={memberRole === r.id}
                      onClick={() => setMemberRole(r.id)}
                      style={{ ...S.segmentBtn, ...(memberRole === r.id ? S.segmentActive : {}) }}
                      data-testid={`signup-role-${r.id}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label style={S.label}>사업자등록번호</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  value={businessNumber}
                  onChange={(e) => handleBusinessNumberInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupBiz(businessNumber); } }}
                  placeholder="000-00-00000"
                  style={{ ...S.formInput, flex: 1, minWidth: 0 }}
                  data-testid="signup-biz-no"
                />
                <button
                  type="button"
                  onClick={() => lookupBiz(businessNumber)}
                  disabled={!businessNumberValid || bizLookup.state === "loading"}
                  data-testid="signup-biz-lookup"
                  style={{ ...S.outlineBtn, width: "auto", padding: "0 14px", whiteSpace: "nowrap", opacity: businessNumberValid ? 1 : 0.5 }}
                >
                  {bizLookup.state === "loading" ? "조회 중…" : "조회"}
                </button>
              </div>
              {bizLookup.state === "done" && (
                <span style={{ ...S.errorText, color: "#1d4ed8" }} data-testid="signup-biz-msg">{bizLookup.msg} — 확인 후 고칠 수 있습니다</span>
              )}
              {bizLookup.state === "fail" && <span style={S.errorText} data-testid="signup-biz-msg">{bizLookup.msg}</span>}
            </div>
            <div>
              <label style={S.label}>이메일</label>
              <input
                value={userEmail}
                onChange={(e) => handleEmailInput(e.target.value)}
                placeholder="example@email.com"
                style={S.formInput}
              />
              {userEmail && !userEmailValid && (
                <span style={S.errorText}>이메일 형식이 올바르지 않습니다</span>
              )}
            </div>
            <div>
              <label style={S.label}>회사명</label>
              <input
                value={corpName}
                onChange={(e) => setCorpName(e.target.value)}
                placeholder="(주)회사명"
                style={S.formInput}
              />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label style={S.label}>대표자명</label>
                <input
                  value={corpCeo}
                  onChange={(e) => setCorpCeo(e.target.value)}
                  placeholder="대표자"
                  style={S.formInput}
                />
              </div>
              <div>
                <label style={S.label}>담당자명</label>
                <input
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="담당자"
                  style={S.formInput}
                />
              </div>
            </div>
            <div>
              <label style={S.label}>주소</label>
              <input
                value={corpAddr}
                onChange={(e) => setCorpAddr(e.target.value)}
                placeholder="서울특별시 ..."
                style={S.formInput}
              />
            </div>
            <div>
              <label style={S.label}>비밀번호</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="4자 이상"
                style={S.formInput}
              />
              {password && password.length < 4 && (
                <span style={S.errorText}>
                  비밀번호는 4자 이상이어야 합니다
                </span>
              )}
            </div>
            <div>
              <label style={S.label}>비밀번호 확인</label>
              <input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="비밀번호 재입력"
                style={S.formInput}
              />
              {passwordConfirm && password !== passwordConfirm && (
                <span style={S.errorText}>비밀번호가 일치하지 않습니다</span>
              )}
            </div>
          </>
        )}
      </div>

      {isLoginHSProduct() && isCompany && (
        <div style={{ marginTop: "8px" }} data-testid="signup-disclosure">
          <label style={S.checkboxLabel}>
            <input
              type="checkbox"
              checked={agreeDisclosure}
              onChange={(e) => setAgreeDisclosure(e.target.checked)}
              style={{ marginRight: "8px", accentColor: DARK }}
              data-testid="signup-disclosure-check"
            />
            <span>
              신고이력 정보공개(신고비율 수준)에 동의합니다{" "}
              <span style={{ color: "#9ca3af" }}>— 선택 · 세번별 신고비율·건수만 익명 통계로, 개별 건·거래처·규격은 비공개</span>{" "}
              <button
                type="button"
                onClick={() => setShowDisclosureText((v) => !v)}
                style={{ border: "none", background: "none", color: "#1d4ed8", cursor: "pointer", padding: 0, font: "inherit", textDecoration: "underline dotted" }}
                data-testid="signup-disclosure-toggle"
              >
                {showDisclosureText ? "접기" : "전문 보기"}
              </button>
            </span>
          </label>
          {showDisclosureText && (
            <div style={{ margin: "6px 0 0 24px", padding: "8px 10px", borderLeft: "2px solid #e5e7eb", fontSize: 12, color: "#4b5563", lineHeight: 1.6 }} data-testid="signup-disclosure-text">
              <div style={{ fontWeight: 700 }}>{CONSENT_TITLE}</div>
              <ol style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                {CONSENT_LINES.map((ln, i) => <li key={i}>{ln}</li>)}
              </ol>
              <div style={{ marginTop: 4, color: "#9ca3af" }}>가입 뒤 첫 로그인 때 회사(사업자번호) 이름으로 기록되며, 회사·정보공개 화면에서 언제든 철회할 수 있습니다.</div>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: "12px" }}>
        <button
          onClick={() => setStep("start")}
          style={{ ...S.outlineBtn, flex: 1 }}
        >
          이전
        </button>
        <button
          onClick={goAuth}
          disabled={loading}
          style={{ ...S.primaryBtn, flex: 2 }}
        >
          {loading ? "처리중..." : "다음"}
        </button>
      </div>
    </div>
  );

  // ─── Step 3: 이메일 인증 ─────────────────────────────────
  const renderAuth = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        animation: "fadeIn 0.3s ease",
      }}
    >
      <h1 style={{ ...S.title, fontSize: "22px" }}>이메일 인증</h1>
      <p style={S.subtitle}>이메일로 전송된 6자리 인증번호를 입력해주세요</p>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          marginBottom: "8px",
        }}
      >
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <input
            key={i}
            type="text"
            maxLength={1}
            value={authNum[i] || ""}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "");
              if (!val && e.nativeEvent.inputType === "deleteContentBackward") {
                const newAuth = authNum.slice(0, i) + authNum.slice(i + 1);
                setAuthNum(newAuth);
                if (i > 0) e.target.previousSibling?.focus();
                return;
              }
              const newAuth = authNum.slice(0, i) + val + authNum.slice(i + 1);
              setAuthNum(newAuth.slice(0, 6));
              if (val && i < 5) e.target.nextSibling?.focus();
            }}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !authNum[i] && i > 0)
                e.target.previousSibling?.focus();
            }}
            style={S.otpInput}
          />
        ))}
      </div>

      <p
        style={{
          textAlign: "center",
          fontSize: "13px",
          color: "#6b7280",
          marginBottom: "16px",
        }}
      >
        인증 코드는 {formatTime(timeLeft)}이 지나면 만료됩니다.
      </p>

      <button
        onClick={handleSignup}
        disabled={loading}
        style={{ ...S.primaryBtn, marginBottom: "8px" }}
      >
        {loading ? "처리 중..." : "인증하기"}
      </button>
      <button
        onClick={sendAuthEmail}
        style={{ ...S.outlineBtn, marginBottom: "8px" }}
      >
        인증번호 재전송
      </button>

      <div style={S.bottomLinks}>
        <span style={{ color: "#9ca3af" }}>문제가 있으신가요?</span>
        <button
          onClick={() => onNavigate && onNavigate("login")}
          style={S.linkBtn}
        >
          고객센터 문의하기
        </button>
      </div>
    </div>
  );

  // ─── Main Render ─────────────────────────────────────
  const stepMap = { start: renderStart, form: renderForm, auth: renderAuth };
  const STEPS = ["start", "form", "auth"];

  return (
    <div style={S.page}>
      {loading && (
        <div style={S.loadingOverlay}>
          <div style={S.spinner} />
        </div>
      )}

      {/* Step indicator */}
      <div
        style={{
          position: "absolute",
          top: "32px",
          display: "flex",
          gap: "8px",
        }}
      >
        {STEPS.map((s, i) => (
          <div
            key={s}
            style={{
              width: "40px",
              height: "3px",
              borderRadius: "2px",
              background: STEPS.indexOf(step) >= i ? DARK : "#e5e7eb",
              transition: "background 0.3s",
            }}
          />
        ))}
      </div>

      <div style={S.card}>
        {/* Logo — LoginHS 브랜드 마크 (다크 라운드 사각 + D 글리프) */}
        <div style={S.logoWrap}>
          <span style={S.logoIcon}>
            <svg
              width="19"
              height="19"
              viewBox="0 0 48 48"
              fill="none"
              stroke="#fff"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <g stroke="#fff" strokeWidth="5">
                  <line x1="14" y1="12" x2="14" y2="36" />
                  <line x1="34" y1="12" x2="34" y2="36" />
                </g>
                <polyline points="17,24 23,29 31,18" stroke="#4285F4" strokeWidth="4.8" fill="none" />
            </svg>
          </span>
          <span style={S.logoText}>{BRAND}</span>
        </div>
        {stepMap[step]()}
      </div>

      <Footer />
    </div>
  );
};

/* ── Styles — LoginHS 절제 톤 (검정·회색) ── */
const S = {
  page: {
    minHeight: "100vh",
    width: "100vw",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    background: "#fafafa",
    fontFamily:
      "'Malgun Gothic', '맑은 고딕', 'Pretendard Variable', 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    padding: "72px 16px 16px",
    boxSizing: "border-box",
  },
  card: {
    maxWidth: "440px",
    width: "100%",
    padding: "32px 32px 24px",
    borderRadius: "16px",
    background: "#fff",
    border: "1px solid #ecedf2",
    boxShadow: "0 2px 12px rgba(20,21,26,0.05)",
    boxSizing: "border-box",
  },
  logoWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "9px",
    marginBottom: "18px",
  },
  logoIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "9px",
    background: DARK,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logoText: {
    fontSize: "21px",
    fontWeight: "800",
    letterSpacing: "-0.4px",
    color: INK,
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    textAlign: "center",
    marginBottom: "6px",
    color: INK,
    letterSpacing: "-0.4px",
  },
  subtitle: {
    fontSize: "13.5px",
    color: "#6b7280",
    textAlign: "center",
    marginBottom: "20px",
  },
  label: {
    display: "block",
    fontSize: "13.5px",
    fontWeight: "600",
    marginBottom: "4px",
    color: "#374151",
  },
  formInput: {
    width: "100%",
    padding: "11px 14px",
    border: "1px solid #dadce0",
    borderRadius: "10px",
    fontSize: "15px",
    outline: "none",
    boxSizing: "border-box",
  },
  primaryBtn: {
    width: "100%",
    padding: "13px",
    border: "none",
    borderRadius: "10px",
    background: DARK,
    color: "#fff",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
  },
  outlineBtn: {
    width: "100%",
    padding: "13px",
    borderRadius: "10px",
    background: "#fff",
    border: "1px solid #dadce0",
    color: "#374151",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
  },
  socialBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    width: "100%",
    padding: "11px",
    borderRadius: "10px",
    background: "#fff",
    border: "1px solid #dadce0",
    color: INK,
    fontSize: "14.5px",
    fontWeight: "600",
    cursor: "pointer",
  },
  socialIcon: {
    width: "22px",
    height: "22px",
    borderRadius: "6px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    margin: "4px 0 14px",
  },
  dividerLine: { flex: 1, height: "1px", background: "#ecedf2" },
  dividerText: { fontSize: "12px", color: "#9ca3af", whiteSpace: "nowrap" },
  errorText: {
    fontSize: "12px",
    color: "#dc2626",
    marginTop: "4px",
    display: "block",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    fontSize: "13.5px",
    color: "#374151",
    cursor: "pointer",
  },
  termsScroll: {
    maxHeight: "240px",
    overflow: "auto",
    border: "1px solid #ecedf2",
    borderRadius: "10px",
    marginTop: "10px",
    background: "#fafbfd",
  },
  termsSectionTitle: {
    fontSize: "14px",
    fontWeight: "600",
    marginTop: "14px",
    marginBottom: "6px",
  },
  segmentWrap: {
    display: "flex",
    background: "#f1f3f8",
    borderRadius: "10px",
    padding: "3px",
    marginBottom: "18px",
  },
  segmentBtn: {
    flex: 1,
    padding: "8px 0",
    border: "none",
    borderRadius: "8px",
    background: "transparent",
    color: "#6b7280",
    fontSize: "13.5px",
    fontWeight: "600",
    cursor: "pointer",
  },
  segmentActive: {
    background: "#fff",
    color: INK,
    boxShadow: "0 1px 3px rgba(20,21,26,0.12)",
  },
  otpInput: {
    width: "46px",
    height: "52px",
    textAlign: "center",
    fontSize: "22px",
    fontWeight: "600",
    border: "1.5px solid #dadce0",
    borderRadius: "10px",
    outline: "none",
  },
  bottomLinks: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "13.5px",
    marginTop: "16px",
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: INK,
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "13.5px",
    padding: 0,
    textDecoration: "underline",
  },
  loadingOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(255,255,255,0.7)",
    zIndex: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #e5e7eb",
    borderTopColor: DARK,
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
};

// Inject keyframes
if (typeof document !== "undefined") {
  if (!document.getElementById("signup-spinner-style")) {
    const s = document.createElement("style");
    s.id = "signup-spinner-style";
    s.textContent = `
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    `;
    document.head.appendChild(s);
  }
}

export default SignupPage;
